import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import DialogContentText from '@mui/material/DialogContentText'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
  useEditSessionDebtMutation,
  useEditSessionVehicleTypeMutation,
  useGetBuildingVehicleTypesQuery,
  useGetSessionsByPlateNumberQuery,
} from '@/api/adminServiceApi'
import { getVehicleTypeLabel } from '@/utils/vehicle'

const EditDebtModalContent = ({queryParams, onEditLoading = undefined, ...props }) => {
  if (!queryParams?.buildingId || !queryParams?.plateNumber) return null;
  const { t } = useTranslation();
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [vehicleTypeEdit, setVehicleTypeEdit] = useState(null);

  const { 
    isLoading: isSearchLoading,
    data: searchResponse,
    error: searchRequestError,
  } = useGetSessionsByPlateNumberQuery(queryParams, {
    skip: !queryParams?.buildingId || !queryParams?.plateNumber,
  });

  const {
    data: vehicleTypesResponse,
    isLoading: isVehicleTypesLoading,
  } = useGetBuildingVehicleTypesQuery(queryParams.buildingId, {
    skip: !queryParams.buildingId,
  });

  const vehicleTypeOptions = useMemo(() => {
    const items = vehicleTypesResponse?.items || []
    const opts = items.map((it) => ({
      value: it.code,
      label: it.label || getVehicleTypeLabel(it.code, t),
    }))
    if (vehicleTypeEdit?.current) {
      const cur = vehicleTypeEdit.current
      const exists = opts.some((o) => o.value.toLowerCase() === String(cur).toLowerCase())
      if (!exists) {
        opts.unshift({ value: cur, label: getVehicleTypeLabel(cur, t) })
      }
    }
    return opts
  }, [vehicleTypesResponse, t, vehicleTypeEdit?.current]);

  const searchRequestErrorMessage = useMemo(() => {
    return searchRequestError?.data?.message || searchRequestError?.message
  }, [searchRequestError]);

  const [editSessionDebt, { 
    data: editDebtResponse,
    isLoading: isEditDebtLoading, 
    error: editDebtRequestError,
    isError: isEditDebtRequestError,
  }] = useEditSessionDebtMutation();

  const [editSessionVehicleType, {
    data: editVehicleTypeResponse,
    isLoading: isEditVehicleTypeLoading,
    error: editVehicleTypeRequestError,
    isError: isEditVehicleTypeRequestError,
  }] = useEditSessionVehicleTypeMutation();
  
  const editDebtRequestErrorMessage = editDebtRequestError?.data?.message || editDebtRequestError?.message
  const rawEditVehicleTypeErrorMessage =
    editVehicleTypeRequestError?.data?.message || editVehicleTypeRequestError?.message
  const editVehicleTypeRequestErrorMessage =
    rawEditVehicleTypeErrorMessage === 'vehicle_type is fixed for this plate'
      ? 'Для этого номера тип ТС закреплён вручную (Оплата → «Закреплённые типы ТС»). Измените или снимите закрепление там.'
      : rawEditVehicleTypeErrorMessage

  const isEditSubmitBtnDisabled = useMemo(() => {
    return isEditDebtLoading 
      || !sessionToEdit?.hasOwnProperty('new_debt_amount') 
      || !(Number(sessionToEdit?.new_debt_amount) >= 0)
  }, [isEditDebtLoading, sessionToEdit]);

  const isVehicleTypeEditDisabled = useMemo(() => {
    if (!vehicleTypeEdit || isEditVehicleTypeLoading) return true
    return String(vehicleTypeEdit.current || '').toLowerCase() === String(vehicleTypeEdit.next || '').toLowerCase()
  }, [vehicleTypeEdit, isEditVehicleTypeLoading]);

  const editDebtRequest = async () => {
    try {
      if (isEditDebtLoading) return;
      const amount = Number(sessionToEdit?.new_debt_amount);
      if (!sessionToEdit) throw new Error('Сессия не выбрана');
      if (amount < 0) throw new Error('Сумма задолженности не может быть отрицательной');
      if (isNaN(amount)) throw new Error('Сумма задолженности должна быть числом');
      await editSessionDebt({
        buildingId: queryParams.buildingId,
        plateNumber: queryParams.plateNumber,
        billingId: sessionToEdit.billing_id,
        debtAmount: amount,
        debtFixed: sessionToEdit.debt_fixed !== false,
        reason: "редактирование задолженности",
      }).unwrap();
      setSessionToEdit(null);
      
    } catch (error) {
      console.error(error)
    }
  }

  const editVehicleTypeRequest = async () => {
    try {
      if (isEditVehicleTypeLoading || !vehicleTypeEdit) return
      await editSessionVehicleType({
        buildingId: queryParams.buildingId,
        billingId: vehicleTypeEdit.billing_id,
        plateNumber: queryParams.plateNumber,
        vehicleType: vehicleTypeEdit.next,
        reason: 'редактирование типа транспорта',
      }).unwrap()
      setVehicleTypeEdit(null)
    } catch (error) {
      console.error(error)
    }
  }

  const isBusy = isEditDebtLoading || isEditVehicleTypeLoading

  useEffect(() => {
    if (onEditLoading) {
      onEditLoading(isBusy);
    }
  }, [isBusy]);

  return (
    <Stack
      direction="column"
      spacing={2}
      {...props}
      sx={[{
        paddingTop: 2
      }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>
      {
        searchRequestError && <Alert color="error">{ searchRequestErrorMessage }</Alert>
      }
      {
        editDebtResponse && Object.keys(editDebtResponse).length > 0 && 
        <Alert 
          color="success"
          sx={{
            whiteSpace: 'pre-line',
            lineHeight: '1',
          }}
        >
          {
            `Данные успешно изменены на:\n
            Общая задолженность: ${editDebtResponse.applied_total},\n
            ${t('debt_remaining')}: ${editDebtResponse.applied_debt},\n
            Оплачено:${editDebtResponse.paid_amount},\n
            Kaspi задолженность: ${editDebtResponse.kaspi_debt_verified},\n
            Округлено: ${editDebtResponse.was_rounded ? 'Да' : 'Нет'}\n
            ${t('billing_debt_fixed')}: ${editDebtResponse.debt_fixed !== false ? t('yes') : t('no')}
            `
          }
        </Alert>
      }
      {
        editVehicleTypeResponse && Object.keys(editVehicleTypeResponse).length > 0 &&
        <Alert color="success" sx={{ whiteSpace: 'pre-line', lineHeight: '1' }}>
          {
            `Тип ТС изменён: ${editVehicleTypeResponse.old_vehicle_type} → ${editVehicleTypeResponse.new_vehicle_type}\n
            Сумма: ${editVehicleTypeResponse.new_amount}\n
            ${t('debt_remaining')}: ${editVehicleTypeResponse.debt_amount}`
          }
        </Alert>
      }
      {
        isEditDebtRequestError && editDebtRequestError && <Alert color="error">{editDebtRequestErrorMessage}</Alert>}
      {
        isEditVehicleTypeRequestError && editVehicleTypeRequestError &&
        <Alert color="error">{editVehicleTypeRequestErrorMessage}</Alert>
      }
      {
        searchResponse && searchResponse.length > 0 && (
          <DialogContentText>{`Результаты поиска ${searchResponse[0].plate_number}:`}</DialogContentText>
        )
      }
      {(isSearchLoading || isBusy) && <LinearProgress />}
      {
        searchResponse && !searchResponse?.length && <Alert color="info">{'по номеру автомобиля не найдено сессий'}</Alert>
      }
      {
        searchResponse && searchResponse.length > 0 && (
          <Stack spacing={1.25}>
            {searchResponse.map((item) => {
              const isDebtEditing = sessionToEdit?.billing_id === item.billing_id
              const isVehicleEditing = vehicleTypeEdit?.billing_id === item.billing_id
              const isEditing = isDebtEditing || isVehicleEditing

              return (
                <Paper
                  key={item.billing_id}
                  elevation={2}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: isEditing ? 'primary.main' : 'divider',
                    borderLeftWidth: 4,
                    borderLeftColor: item.is_open ? 'info.main' : 'success.main',
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      mb: 1
                    }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {
                          item.entrance_time
                            ? format(new Date(item.entrance_time.replace('Z', '')), 'dd.MM.yyyy HH:mm')
                            : '-'
                        }
                      </Typography>
                      {
                        item.is_open
                          ? <Chip size="small" label="Открыта" color="info" />
                          : <Chip size="small" label="Закрыт" color="success" />
                      }
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        alignItems: "center",
                        flexShrink: 0
                      }}>
                      {
                        isVehicleEditing ? (
                          <FormControl size="small" sx={{ minWidth: 180, mt: 0.75 }}>
                            <Select
                              value={vehicleTypeEdit.next}
                              disabled={isEditVehicleTypeLoading}
                              onChange={(e) => setVehicleTypeEdit({
                                ...vehicleTypeEdit,
                                next: e.target.value,
                              })}
                            >
                              {vehicleTypeOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2">
                            {/* <LocalShippingIcon fontSize="small" /> */}
                            { vehicleTypeOptions.find((o) => 
                                o.value.toLowerCase() === String(item.vehicle_type).toLowerCase()
                              )?.label 
                              || getVehicleTypeLabel(item.vehicle_type, t) } 
                          </Typography>
                        )
                      }
                      {
                        !sessionToEdit && !vehicleTypeEdit &&
                          <Tooltip title={t('vehicle_type')}>
                            <IconButton
                              sx={{ p: '4px' }}
                              onClick={() => {
                                const current = String(item.vehicle_type || '').trim() || (vehicleTypeOptions[0]?.value ?? '')
                                setVehicleTypeEdit({
                                  billing_id: item.billing_id,
                                  current,
                                  next: current,
                                })
                              }}
                              disabled={isVehicleTypesLoading || vehicleTypeOptions.length === 0}
                            >
                              <EditIcon 
                                sx={{
                                  fontSize: '1rem'
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                      }
                      {
                        isVehicleEditing && !isEditVehicleTypeLoading &&
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setVehicleTypeEdit(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      }
                      {
                        isVehicleEditing && (
                          <IconButton
                            disabled={isVehicleTypeEditDisabled}
                            size="small"
                            color="success"
                            onClick={editVehicleTypeRequest}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    </Stack>
                  </Stack>
                  <Divider sx={{ mb: 1.25 }} />
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={0.5}>
                      <DebtMetric label={t('amount')} value={item.amount} />
                      <DebtMetric
                        label={t('paid')}
                        value={item.paid_amount}
                        color="success.main"
                      />
                      {
                        !isDebtEditing && (
                          <DebtMetric
                            label={t('debt_remaining')}
                            value={item.debt_amount}
                            color="error.main"
                          />
                        )
                      }
                      {
                        !sessionToEdit && !vehicleTypeEdit &&
                        <Tooltip title="Редактировать задолженность">
                          <IconButton
                            size="small"
                            sx={{
                              alignSelf: 'center',
                            }}
                            onClick={() => {
                              setSessionToEdit({
                                ...item,
                                new_debt_amount: item.debt_amount,
                                debt_fixed: true,
                              });
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    </Stack>
                    {
                      isDebtEditing && (
                        <DebtEditFields 
                          debtFixedValue={sessionToEdit.debt_fixed}
                          onDebtIsFixedChange={(e) => setSessionToEdit({
                            ...sessionToEdit,
                            debt_fixed: e.target.checked,
                          })}
                          isAmountDisabled={isEditDebtLoading}
                          isAmountError={isEditDebtRequestError}
                          amountValue={sessionToEdit?.new_debt_amount ?? ''}
                          onAmountChange={(e) => setSessionToEdit({
                            ...sessionToEdit,
                            new_debt_amount: e.target.value
                          })}
                          onSubmit={() => editDebtRequest()}
                          submitDisabled={isEditSubmitBtnDisabled}
                          isLoading={isEditDebtLoading}
                          onCancel={() => setSessionToEdit(null)}
                        />
                      )
                    }
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )
      }
    </Stack>
  );
} 

const DebtMetric = ({ label, value, color = 'text.primary' }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "block"
      }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color }}>
      {value}
    </Typography>
  </Box>
)

const DebtEditFields = ({
  debtFixedValue,
  onDebtIsFixedChange,
  onAmountChange,
  amountValue,
  isAmountError,
  isAmountDisabled,
  onSubmit,
  submitDisabled,
  isLoading,
  onCancel,
}) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={{
        color: "text.secondary"
      }}>
        { t('debt_remaining') }
      </Typography>
      <TextField
        autoFocus
        size="small"
        value={amountValue}
        disabled={isAmountDisabled}
        error={isAmountError}
        onChange={onAmountChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <Tooltip
          title={debtFixedValue === false ? t('billing_debt_fixed_off_hint') : ''}
        >
          <Box>
            <FormControlLabel
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': { fontSize: 12, lineHeight: 1.3 },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={debtFixedValue !== false}
                  disabled={isLoading}
                  onChange={onDebtIsFixedChange}
                />
              }
              label={t('billing_debt_fixed')}
            />
          </Box>
        </Tooltip>
        <Box>
          {
            !isLoading &&
            <IconButton
              size="small"
              // color="error"
              sx={{
                border: '1px solid',
                borderColor: 'text.secondary'
              }}
              onClick={onCancel}
            >
              <CloseIcon 
                fontSize="small" 
                sx={{ 
                  color: 'text.secondary' 
                }}
              />
            </IconButton>
          }
          {
            <IconButton
              disabled={submitDisabled}
              size="small"
              color="success"
              sx={{
                ml: 1,
                border: '1px solid',
              }}
              onClick={onSubmit}
            >
              <DoneIcon fontSize="small" />
            </IconButton>
            
          }
        </Box>
      </Stack>
    </Stack>
  );
}

export default EditDebtModalContent;
